<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
    context: { type: Object, default: null },
    contextUrl: { type: String, required: true },
    designReferencesUrl: { type: String, required: true },
    readOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['update', 'close']);

const tone = ref('professional');
const audience = ref('');
const topic = ref('');
const cta = ref('');
const language = ref('');
const designReferenceContentId = ref(null);
const designReferences = ref([]);
const saving = ref(false);
const saved = ref(false);

const toneOptions = [
    { value: 'minimal', labelKey: 'page:aiPageBuilder.onboarding.tone.minimal' },
    { value: 'professional', labelKey: 'page:aiPageBuilder.onboarding.tone.professional' },
    { value: 'provoking', labelKey: 'page:aiPageBuilder.onboarding.tone.provoking' },
];

const loadFromContext = (ctx) => {
    if (!ctx) return;
    tone.value = ctx.tone || 'professional';
    audience.value = ctx.audience || '';
    topic.value = ctx.topic || '';
    cta.value = ctx.cta || '';
    language.value = ctx.language || '';
    designReferenceContentId.value = ctx.design_reference_content_id || null;
};

const loadDesignReferences = async () => {
    try {
        const res = await axios.get(props.designReferencesUrl);
        designReferences.value = res.data.pages || [];
    } catch (e) {
        // Silently fail
    }
};

const handleSave = async () => {
    if (props.readOnly) return;

    saving.value = true;
    saved.value = false;

    const contextData = {
        tone: tone.value,
        audience: audience.value.trim() || null,
        topic: topic.value.trim() || null,
        cta: cta.value.trim() || null,
        enable_interactive_features: true,
        language: language.value.trim() || null,
        design_reference_content_id: designReferenceContentId.value || null,
    };

    try {
        await axios.post(props.contextUrl, contextData);
        saved.value = true;
        setTimeout(() => { saved.value = false; }, 2000);
        emit('update', contextData);
    } catch (e) {
        // Error handling
    } finally {
        saving.value = false;
    }
};

watch(() => props.context, (ctx) => {
    loadFromContext(ctx);
}, { immediate: true });

onMounted(() => {
    loadDesignReferences();
});
</script>

<template>
    <div class="flex flex-col h-full overflow-y-auto">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">
                <mf-translate domainkey="page:aiPageBuilder.context.settings"></mf-translate>
            </h3>
            <button-base @click="emit('close')" style-type="default" size="xs" class="!border-0 !bg-transparent !px-1 !py-1 !text-gray-400">
                <template #icon>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </template>
            </button-base>
        </div>

        <div class="p-4 space-y-4">
            <!-- Topic -->
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.topicLabel"></mf-translate>
                </label>
                <textarea
                    v-model="topic"
                    :readonly="readOnly"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.topicPlaceholder')"
                    rows="2"
                    :class="[
                        'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm resize-none',
                        readOnly ? 'bg-gray-50 text-gray-500' : 'focus:border-primary focus:ring-1 focus:ring-primary',
                    ]"
                ></textarea>
            </div>

            <!-- Audience -->
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.audienceLabel"></mf-translate>
                </label>
                <textarea
                    v-model="audience"
                    :readonly="readOnly"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.audiencePlaceholder')"
                    rows="2"
                    :class="[
                        'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm resize-none',
                        readOnly ? 'bg-gray-50 text-gray-500' : 'focus:border-primary focus:ring-1 focus:ring-primary',
                    ]"
                ></textarea>
            </div>

            <!-- Call to action -->
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.ctaLabel"></mf-translate>
                </label>
                <textarea
                    v-model="cta"
                    :readonly="readOnly"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.ctaPlaceholder')"
                    rows="2"
                    :class="[
                        'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm resize-none',
                        readOnly ? 'bg-gray-50 text-gray-500' : 'focus:border-primary focus:ring-1 focus:ring-primary',
                    ]"
                ></textarea>
            </div>

            <!-- Tone -->
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.toneLabel"></mf-translate>
                </label>
                <div class="flex gap-1.5">
                    <component
                        v-for="option in toneOptions"
                        :key="option.value"
                        :is="tone === option.value ? 'button-primary' : 'button-default'"
                        @click="!readOnly && (tone = option.value)"
                        type="button"
                        size="sm"
                        :disabled="readOnly"
                        class="flex-1 justify-center"
                        :class="tone === option.value && readOnly ? 'font-bold' : ''"
                    >
                        <mf-translate :domainkey="option.labelKey"></mf-translate>
                    </component>
                </div>
            </div>

            <!-- Language -->
            <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.languageLabel"></mf-translate>
                </label>
                <input
                    v-model="language"
                    type="text"
                    :readonly="readOnly"
                    :placeholder="_mft('page:aiPageBuilder.onboarding.languagePlaceholder')"
                    :class="[
                        'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm',
                        readOnly ? 'bg-gray-50 text-gray-500' : 'focus:border-primary focus:ring-1 focus:ring-primary',
                    ]"
                />
            </div>

            <!-- Design reference -->
            <div v-if="designReferences.length > 0">
                <label class="block text-xs font-medium text-gray-700 mb-1">
                    <mf-translate domainkey="page:aiPageBuilder.onboarding.designReference"></mf-translate>
                </label>
                <select
                    v-model="designReferenceContentId"
                    :disabled="readOnly"
                    :class="[
                        'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm',
                        readOnly ? 'bg-gray-50 text-gray-500' : 'focus:border-primary focus:ring-1 focus:ring-primary',
                    ]"
                >
                    <option :value="null">
                        <mf-translate domainkey="page:aiPageBuilder.onboarding.noReference"></mf-translate>
                    </option>
                    <option
                        v-for="page in designReferences"
                        :key="page.block_editor_content_id"
                        :value="page.block_editor_content_id"
                    >
                        {{ page.title }}
                    </option>
                </select>
            </div>

            <!-- Save button -->
            <button-primary
                v-if="!readOnly"
                @click="handleSave"
                type="button"
                :disabled="saving"
                size="lg"
                class="w-full justify-center"
            >
                <span v-if="saved">
                    <mf-translate domainkey="page:aiPageBuilder.context.saved"></mf-translate>
                </span>
                <span v-else>
                    <mf-translate domainkey="page:aiPageBuilder.components.save"></mf-translate>
                </span>
            </button-primary>
        </div>
    </div>
</template>
